import { useState, useRef, useCallback } from "preact/hooks";
import type { InlineConfig } from "../shared/types.js";
import { submitPhoneNumber, FormVueApiError } from "../shared/api.js";
import { formatPhoneInput, validatePhoneNumber } from "../shared/validation.js";
import { trackClick } from "../shared/tracking.js";
import {
  captureCallRail,
  prepareCallRailCapture,
} from "../shared/callrail.js";

type WidgetState = "button" | "input" | "submitting" | "success" | "error";

const SUBMIT_ICON_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-left: 4px; vertical-align: middle;"><polyline points="9,18 15,12 9,6"></polyline></svg>`;
const ARROW_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>`;

const T = "0.5s ease-in-out";

interface InlineWidgetProps {
  config: InlineConfig;
}

export function InlineWidget({ config }: InlineWidgetProps) {
  const [state, setState] = useState<WidgetState>("button");
  const phoneRef = useRef<HTMLInputElement>(null);
  const [inputColor, setInputColor] = useState("#333");

  const isButton = state === "button";
  const isInput = !isButton;
  const isSuccess = state === "success";
  const isSubmitting = state === "submitting";
  const isOutline = config.variant === "outline";
  const isPrimary = config.variant === "primary";
  const isNewLayout = isPrimary || isOutline;

  const submitRef = useRef(handleSubmit);
  submitRef.current = handleSubmit;

  const handleAction = useCallback(() => {
    if (state === "button") {
      if (!config.preview) {
        trackClick(config.embedApiUrl, config.formId, config.embed, config.attribution);
      }
      setState("input");
    } else if (state === "input" || state === "error") {
      submitRef.current();
    }
  }, [state, config]);

  function handlePhoneInput(e: Event) {
    const input = e.target as HTMLInputElement;
    setInputColor("#333");

    const currentValue = input.value;
    if (currentValue === "") return;

    const cursorPosition = input.selectionStart ?? currentValue.length;
    const digitsBefore = currentValue.substring(0, cursorPosition).replace(/\D/g, "").length;
    const formatted = formatPhoneInput(currentValue);

    if (formatted !== currentValue) {
      input.value = formatted;
      let newPos = 0;
      let digitCount = 0;
      for (let i = 0; i < formatted.length && digitCount < digitsBefore; i++) {
        newPos++;
        if (/\d/.test(formatted[i])) digitCount++;
      }
      const digitsTotal = formatted.replace(/\D/g, "").length;
      if (digitsBefore >= digitsTotal) newPos = formatted.length;
      input.setSelectionRange(newPos, newPos);
    }
  }

  function handlePaste(e: ClipboardEvent) {
    e.preventDefault();
    const input = phoneRef.current;
    if (!input) return;
    const pastedText = e.clipboardData?.getData("text") ?? "";
    const formatted = formatPhoneInput(pastedText);
    input.value = formatted;
    input.setSelectionRange(formatted.length, formatted.length);
  }

  async function handleSubmit() {
    if (state === "submitting") return;
    const input = phoneRef.current;
    if (!input) return;

    const phoneNumber = input.value.trim();

    if (!phoneNumber) {
      showError("Phone number required");
      return;
    }

    const validation = validatePhoneNumber(phoneNumber);
    if (!validation.isValid || !validation.formatted) {
      showError("Invalid US phone number");
      return;
    }

    const callRailCapture = prepareCallRailCapture(config.attribution.callrail, {
      phone: validation.formatted,
    });
    const payload = {
      formId: config.formId,
      entityId: validation.formatted,
      phoneNumber: validation.formatted,
      utmSource: config.attribution.utm.source,
      ...(Object.keys(config.prefill).length ? { preData: config.prefill } : {}),
      campaignId: config.attribution.campaignId,
      campaignSlug: config.attribution.campaignSlug,
      utm: config.attribution.utm,
      metadata: {
        ...config.embed,
        ...config.attribution.metadata,
        ...(callRailCapture ? { callrail: callRailCapture.metadata } : {}),
      },
    };

    config.onSubmit?.(validation.formatted);
    setState("submitting");

    if (config.preview) {
      config.onPreviewSubmit?.(payload);
      setState("success");
      return;
    }

    try {
      const data = await submitPhoneNumber(config.embedApiUrl, payload);

      if (callRailCapture) {
        void captureCallRail(callRailCapture);
      }
      setState("success");
      config.onSuccess?.(data);
    } catch (err) {
      setState("error");

      const isValidationError =
        err instanceof FormVueApiError &&
        (err.status === 400 || err.status === 404 || err.status === 422);
      const message = isValidationError
        ? (err as FormVueApiError).message
        : "Error - please try again";
      showError(message);

      const error = err instanceof Error ? err : new Error("Unknown error");
      config.onError?.(error);
    }
  }

  function showError(message: string) {
    setState("error");
    const input = phoneRef.current;
    if (!input) return;
    setInputColor("#ff0000");
    input.placeholder = message;
    input.value = "";
  }

  function handlePhoneTransitionEnd(e: TransitionEvent) {
    if (state === "input" && e.propertyName === "flex-grow" && e.target === phoneRef.current) {
      phoneRef.current?.focus();
    }
  }

  return (
    <div
      class={`text-center ${config.width === "full" ? "w-full" : "w-fit"}`}
      style={config.glass ? {
        background: "rgba(255,255,255,0.1)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        borderRadius: `calc(${config.borderRadius}px + 6px)`,
        padding: "20px",
        boxShadow: "0 8px 32px 0 rgba(31,38,135,0.15)",
      } : undefined}
    >
      {/* Label */}
      {config.label && (
        <div
          class={`font-medium ${isNewLayout ? "text-center" : "text-left"} mb-2`}
          style={{
            color: config.colors.label,
            fontSize: `${config.fontSize * 0.8}px`,
          }}
        >
          {config.label}
        </div>
      )}

      {/* Body */}
      <div class="flex items-center w-full relative">
        {/* Logo (absolutely positioned) */}
        <div
          class="flex items-center justify-center absolute top-0 z-[3] cursor-pointer"
          style={{
            backgroundColor: config.colors.logo,
            borderRadius: `${config.borderRadius}px`,
            height: `${config.height}px`,
            padding: `${(config.height - config.logoSize) / 2}px`,
            left: isButton && !isNewLayout ? `calc(100% - ${config.height}px)` : "0px",
            width: isButton && !isNewLayout ? undefined : `${config.height}px`,
            minWidth: isButton && !isNewLayout ? undefined : `${config.height}px`,
            border: isButton && !isNewLayout ? config.buttonBorder : "none",
            transition: `left ${T}, width ${T}, min-width ${T}, padding ${T}`,
          }}
          onClick={handleAction}
        >
          <img
            src={config.logoUrl}
            alt="FormVue"
            class="shrink-0"
            style={{ width: `${config.logoSize}px`, height: `${config.logoSize}px` }}
          />
        </div>

        {/* Input area */}
        <div
          class="flex items-center overflow-hidden flex-auto z-[2] relative"
          style={{
            borderRadius: `${config.borderRadius}px`,
            height: `${config.height}px`,
            background: isButton ? "transparent" : "white",
            boxShadow: isButton ? "none" : "0 2px 8px rgba(0,0,0,0.1)",
            border: isButton ? "none" : config.inputBorder,
            transition: `background-color ${T}, box-shadow ${T}`,
          }}
        >
          {/* Spacer for logo */}
          <div
            class="shrink-0 overflow-hidden"
            style={{
              width: isButton ? "0px" : `${config.height - 8}px`,
              transition: `width ${T}`,
            }}
          />

          {/* Phone input */}
          <input
            ref={phoneRef}
            type="tel"
            name="phoneNumber"
            autocomplete="tel"
            inputmode="tel"
            enterkeyhint="go"
            placeholder="Enter phone number"
            aria-label="Phone number"
            class="border-0 bg-transparent outline-none min-w-0 h-full"
            style={{
              fontSize: `${config.fontSize * 0.875}px`,
              fontFamily: "system-ui, -apple-system, sans-serif",
              color: inputColor,
              flex: isButton ? "0 0 0px" : "1 1 auto",
              padding: isButton ? "0" : "8px 12px",
              opacity: isSuccess || isSubmitting ? 0 : isButton ? 0 : 1,
              pointerEvents: isButton || isSuccess ? "none" : "auto",
              transition: `flex ${T}, padding ${T}, opacity ${T}`,
            }}
            onInput={handlePhoneInput}
            onPaste={handlePaste}
            onTransitionEnd={handlePhoneTransitionEnd}
            onKeyPress={(e: KeyboardEvent) => { if (e.key === "Enter") handleSubmit(); }}
          />

          {/* Action button (CTA / Submit) */}
          <button
            type="button"
            class="border-0 cursor-pointer flex items-center justify-center font-medium overflow-hidden h-full whitespace-nowrap shrink-0 p-0 hover:brightness-90 disabled:cursor-not-allowed"
            style={{
              backgroundColor: isButton && isOutline ? "white" : isButton && isPrimary ? "#0a0a0a" : config.colors.button,
              color: isButton && isOutline ? "#0a0a0a" : isButton && isPrimary ? "#fafafa" : config.colors.buttonText,
              borderRadius: `${config.borderRadius}px`,
              fontFamily: "system-ui, -apple-system, sans-serif",
              flex: isButton ? "1 1 auto" : "0 0 auto",
              border: isButton ? (isOutline ? "1px solid #e5e5e5" : config.buttonBorder) : "none",
              paddingLeft: isButton && isNewLayout ? `${config.height + 12}px` : "0",
              paddingRight: isButton && !isNewLayout ? `${config.height + 12}px` : "0",
              gap: isButton && isNewLayout ? "16px" : undefined,
              opacity: isSuccess || isSubmitting ? 0 : 1,
              pointerEvents: isSuccess || isSubmitting ? "none" : "auto",
              transition: `flex ${T}, padding ${T}, filter 0.2s ease`,
            }}
            onClick={handleAction}
            disabled={isSubmitting}
          >
            {/* CTA text */}
            <span
              class="flex items-center justify-center whitespace-nowrap"
              style={{
                fontSize: `${config.fontSize}px`,
                opacity: isButton ? 1 : 0,
                flex: isButton ? "1" : "0 0 0px",
                width: isButton ? undefined : "0",
                padding: isButton ? (isNewLayout ? "0" : "0 0 0 12px") : "0",
                overflow: "hidden",
                transition: `opacity 0.25s ease-out, flex 0.3s ease, padding 0.3s ease`,
              }}
            >
              {config.text}
            </span>

            {/* Arrow icon (primary/outline only) */}
            <span
              class="flex items-center shrink-0"
              style={{
                opacity: isNewLayout && isButton ? 1 : 0,
                width: isNewLayout && isButton ? undefined : "0",
                paddingRight: isNewLayout && isButton ? "16px" : "0",
                overflow: "hidden",
                transition: `opacity 0.25s ease-out, width 0.3s ease, padding 0.3s ease`,
              }}
              dangerouslySetInnerHTML={{ __html: ARROW_SVG }}
            />

            {/* Submit text */}
            <span
              class="flex items-center whitespace-nowrap"
              style={{
                fontSize: `${config.fontSize * 0.875}px`,
                opacity: isButton ? 0 : 1,
                width: isButton ? "0" : undefined,
                padding: isButton ? "0" : "0 16px",
                overflow: isButton ? "hidden" : undefined,
                transition: `opacity 0.25s ease-in 0.25s, width 0.3s ease, padding 0.3s ease`,
              }}
              dangerouslySetInnerHTML={{ __html: `Send Link ${SUBMIT_ICON_SVG}` }}
            />
          </button>

          {/* Submitting overlay */}
          <div
            class="absolute inset-0 flex items-center justify-center gap-2 p-3 font-medium text-center"
            style={{
              color: "#333",
              fontFamily: "system-ui, -apple-system, sans-serif",
              fontSize: `${config.fontSize * 0.875}px`,
              opacity: isSubmitting ? 1 : 0,
              pointerEvents: "none",
              transition: "opacity 0.3s ease-in",
            }}
          >
            <div
              class="shrink-0 rounded-full animate-[fv-spin_0.6s_linear_infinite]"
              style={{
                width: "16px",
                height: "16px",
                border: "2px solid #d1d5db",
                borderTopColor: "#333",
              }}
            />
            Sending link...
          </div>

          {/* Success message */}
          <div
            class="absolute inset-0 flex items-center justify-center p-3 font-medium text-center"
            style={{
              color: "#333",
              fontFamily: "system-ui, -apple-system, sans-serif",
              fontSize: `${config.fontSize * 0.875}px`,
              opacity: isSuccess ? 1 : 0,
              pointerEvents: isSuccess ? "auto" : "none",
              transition: "opacity 0.3s ease-in",
            }}
          >
            <span class="text-[#007145] mr-2" style={{ fontSize: `${config.fontSize * 1.25}px` }}>✓</span>
            Link sent! Check your phone.
          </div>
        </div>
      </div>

      {/* Help text */}
      <div
        class="text-center mt-2 overflow-hidden"
        style={{
          fontSize: `${config.fontSize * 0.75}px`,
          color: config.colors.label,
          fontFamily: "system-ui, -apple-system, sans-serif",
          opacity: state === "input" || state === "error" ? 1 : 0,
          maxHeight: state === "input" || state === "error" ? "40px" : "0px",
          transition: `opacity 0.4s ease-in 0.3s, max-height 0.4s ease-in 0.3s`,
        }}
      >
        We'll text you a personalized link to get started
      </div>
    </div>
  );
}
